import axios, { AxiosInstance } from 'axios';
import {
  getNotaAdminApiBaseUrl,
  getNotaAppId,
  isNotaAuthReady,
} from '../config/notaAuth';

/**
 * Parallel client for the central Nota Admin Login API.
 *
 * Scope of this Step 2:
 *   - login (status === 'success' only)
 *   - refresh (rotates BOTH tokens)
 *   - logout (Bearer auth, no body)
 *
 * Out of scope (deliberately stubbed with clear "not implemented" results so
 * the architecture doesn't block adding them later):
 *   - 2fa-required
 *   - passkey-required
 *   - setup-required
 *   - direct passwordless passkey login
 *   - settings/security management endpoints
 *
 * This module owns its own axios instance with NO interceptors so it can't
 * accidentally pick up the local-auth interceptors in `services/api.ts`.
 * Token storage uses the existing localStorage keys (`access_token`,
 * `refresh_token`, `token_expiry`) so the rest of the app — `api.ts` request
 * interceptor, `AuthContext` bootstrap, `ProtectedRoute` — keeps working
 * unchanged.
 */

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Mirrors the Nota guide §2 response shape. Other status codes (`2fa-required`,
 * `passkey-required`, `setup-required`, `failed`) include extra fields like
 * `temporaryToken` / `setupToken` / `availableMfaTypes`; we type them loosely
 * here because we only consume `success` for now.
 */
export type NotaLoginStatus =
  | 'success'
  | '2fa-required'
  | 'passkey-required'
  | 'setup-required'
  | 'failed';

export interface NotaLoginRawResponse {
  status: NotaLoginStatus;
  token?: string;
  refreshToken?: string;
  // The fields below are present on non-success statuses and are intentionally
  // unused in Step 2. Listed here as documentation for the next iteration.
  temporaryToken?: string;
  setupToken?: string;
  mfaType?: 'EMAIL' | 'AUTHENTICATOR';
  availableMfaTypes?: Array<'EMAIL' | 'AUTHENTICATOR'>;
  errorCode?: string;
  message?: string;
}

export interface NotaRefreshResponse {
  token: string;
  refreshToken: string;
}

/**
 * Discriminated result from `notaLogin`. Callers MUST check `.kind`. Step 2
 * supports only `success`; every other variant carries enough information for
 * a clear error message and a TODO marker for the future implementation.
 *
 * The `success` variant intentionally does NOT include the AdminUser. The
 * Nota token alone doesn't carry role/assignedVenueIds — those are local
 * wine-api concepts. Callers must hydrate via `meService.fetchCurrentUser()`
 * after a `success` to populate the user state.
 */
export type NotaLoginResult =
  | { kind: 'success'; token: string; refreshToken: string }
  | { kind: 'mfa-required'; message: string; mfaType?: string }
  | { kind: 'passkey-required'; message: string }
  | { kind: 'setup-required'; message: string }
  | { kind: 'failed'; message: string; errorCode?: string };

// ── Internal helpers ─────────────────────────────────────────────────────────

let notaClient: AxiosInstance | null = null;

const requireNotaClient = (): AxiosInstance => {
  if (!isNotaAuthReady()) {
    throw new Error(
      'Nota auth is not configured. Set REACT_APP_NOTA_AUTH_ENABLED=true and REACT_APP_NOTA_ADMIN_API_BASE_URL.',
    );
  }
  if (notaClient) return notaClient;

  const baseURL = getNotaAdminApiBaseUrl()!; // non-null by isNotaAuthReady()
  notaClient = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
    // Reasonable cap so a slow IdP can't hang the login form indefinitely.
    timeout: 15_000,
  });
  return notaClient;
};

const persistTokens = (token: string, refreshToken: string): void => {
  // Reuse the same localStorage keys the local flow uses so the api.ts request
  // interceptor (which reads `access_token`) keeps working without any change.
  localStorage.setItem('access_token', token);
  localStorage.setItem('refresh_token', refreshToken);
  // We don't get expiresIn from Nota in the documented response. Setting a
  // sentinel that pushes the proactive-refresh window past "now" — refresh
  // will be triggered reactively on the first 401 instead.
  localStorage.removeItem('token_expiry');
};

const clearTokens = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token_expiry');
};

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * POST /admin/login per the Nota guide §2. Returns a discriminated result.
 *
 * - `success` → tokens persisted; caller is responsible for hydrating the
 *   AdminUser via `meService.fetchCurrentUser()` (the wine-api owns role +
 *   assignedVenueIds; the Nota token does not carry them).
 * - `2fa-required` / `passkey-required` / `setup-required` → no tokens stored;
 *   the caller surfaces a placeholder error message until the matching screen
 *   is implemented.
 * - `failed` → error code + message from the server.
 *
 * Network / non-2xx errors throw — they aren't part of the documented status
 * vocabulary and need different treatment (retry, offline banner, etc.) at
 * the call site.
 */
export const notaLogin = async (
  email: string,
  password: string,
): Promise<NotaLoginResult> => {
  const client = requireNotaClient();

  const deviceSessionId = localStorage.getItem('nota_device_session_id') ?? undefined;
  const body = {
    email,
    password,
    appId: getNotaAppId(),
    ...(deviceSessionId ? { deviceSessionId } : {}),
  };

  let raw: NotaLoginRawResponse;
  try {
    const response = await client.post<NotaLoginRawResponse>('/admin/login', body);
    raw = response.data;
  } catch (error: any) {
    // Map the documented HTTP-level errors (401 with errorCode) to the
    // discriminated result so callers don't have to inspect axios errors.
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 401 && data) {
      return {
        kind: 'failed',
        message: data.message ?? 'Invalid email or password.',
        errorCode: data.errorCode,
      };
    }
    throw error;
  }

  switch (raw.status) {
    case 'success': {
      if (!raw.token || !raw.refreshToken) {
        return {
          kind: 'failed',
          message: 'Login response missing token or refreshToken.',
        };
      }
      persistTokens(raw.token, raw.refreshToken);
      // Caller must immediately hydrate the AdminUser via `meService` and
      // store it under `localStorage.user`. We deliberately do not stash any
      // placeholder here — better to render a loading state while hydration
      // is in flight than to seed a wrong role/venue list.
      return { kind: 'success', token: raw.token, refreshToken: raw.refreshToken };
    }
    case '2fa-required':
      return {
        kind: 'mfa-required',
        message:
          '2FA is required for this account. The 2FA verification screen has not been implemented yet (Step 2 limitation).',
        mfaType: raw.mfaType,
      };
    case 'passkey-required':
      return {
        kind: 'passkey-required',
        message:
          'Passkey is required for this account. The passkey verification screen has not been implemented yet (Step 2 limitation).',
      };
    case 'setup-required':
      return {
        kind: 'setup-required',
        message:
          'First-time setup is required. The setup wizard has not been implemented yet (Step 2 limitation).',
      };
    case 'failed':
    default:
      return {
        kind: 'failed',
        message: raw.message ?? 'Login failed.',
        errorCode: raw.errorCode,
      };
  }
};

/**
 * POST /admin/refresh-token per the Nota guide §7. Critical: BOTH the access
 * token AND the refresh token are rotated on every call — the response's
 * refreshToken replaces the one we sent up. Callers must persist both halves.
 */
export const notaRefresh = async (): Promise<NotaRefreshResponse> => {
  const client = requireNotaClient();
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) throw new Error('No refresh token available');

  const response = await client.post<NotaRefreshResponse>('/admin/refresh-token', {
    refreshToken,
  });

  if (!response.data?.token || !response.data?.refreshToken) {
    throw new Error('Refresh response missing token or refreshToken');
  }

  persistTokens(response.data.token, response.data.refreshToken);
  return response.data;
};

/**
 * POST /admin/logout per the Nota guide §7. Authenticated by `Authorization:
 * Bearer <token>`, no body. Always clears local tokens, even if the network
 * call fails — better to be logged out client-side than to leave dangling
 * tokens after a user clicked Sign Out.
 */
export const notaLogout = async (): Promise<void> => {
  const client = requireNotaClient();
  const token = localStorage.getItem('access_token');

  try {
    if (token) {
      await client.post(
        '/admin/logout',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    }
  } catch (err) {
    // Swallow — local clear below is the user-facing source of truth.
    console.warn('Nota logout API call failed; clearing local tokens anyway.', err);
  } finally {
    clearTokens();
    localStorage.removeItem('user');
    localStorage.removeItem('password_change_required');
  }
};

// Test/debug seam: lets callers force a fresh axios client after env-var
// changes in tests. Not used in production paths.
export const __resetNotaClient = (): void => {
  notaClient = null;
};
